import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { JwtLegFiClaims } from '../../../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../../../services/auth/legfi-jwt.service';
import { GrowlerService } from '../../../../shared/growler/growler.service';
import { PlaidService, PlaidSuccessMetaData } from '../../../../../services/plaid.service';
import conditionalValidator from '../../../../../directives/validators/conditional.validator';
import { PlaidAccount, PlaidToken } from '../../../../../models/entities/plaid-token';
import { PlaidTokenService } from '../../../../../services/plaid-token.service';
import { PlaidSearchDialogComponent, PlaidSearchDialogResult } from '../plaid-search-dialog/plaid-search-dialog.component';
import { ExistingBankConnectionChoice, ExistingBankConnectionDialogComponent } from '../existing-bank-connection-dialog/existing-bank-connection-dialog.component';
import { ConfirmWithSelectionDialogComponent, ConfirmWithSelectionDialogInput, ConfirmWithSelectionDialogResult } from '../../confirm-with-selection-dialog/confirm-with-selection-dialog.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UnifiedBankAccount } from '../../../../../models/entities/unified-bank-account';
import moment from 'moment-timezone';
import { RequestDate } from '../../../../shared/http/request-interfaces';
import type { PhSelectItem } from '@payhoa/ui-kit/elements';
import { SharedModule } from '../../../../shared/shared.module';
import { ControlMessagesComponent } from '../../../../shared/control-messages/control-messages.component';
import { SuperStarComponent } from '../../super-star/super-star.component';

declare var Plaid: any;

const NEW_LOGIN = 'new-login';
const NEW_ACCOUNT = 'new-account';

export interface BankAccountPropertiesFormValue
{
    id?: number;
    friendlyName: string;
    useForDeposits: boolean;
    routingNumber: string;
    accountNumber: string;
    connectToTransactionFeed: boolean;
    plaidTokenId: number;
    plaidAccountId: string;
    plaidImportAfter?: RequestDate;
}

/**
 * Manages bank account configuration for deposits and transaction feed connections.
 * Orchestrates complex Plaid integration flows for linking multiple banking credentials.
 *
 * Plaid connection hierarchy: Organization can have multiple PlaidTokens (banking logins),
 * each token can expose multiple PlaidAccounts, but each account maps to one UnifiedBankAccount.
 * Users can either add accounts to existing tokens or create new banking connections.
 */
@UntilDestroy()
@Component({
    selector: 'app-bank-account-properties',
    templateUrl: './bank-account-properties.component.html',
    standalone: true,
    imports: [
        SharedModule,
        ControlMessagesComponent,
        SuperStarComponent,
        ConfirmWithSelectionDialogComponent,
        PlaidSearchDialogComponent,
        ExistingBankConnectionDialogComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BankAccountPropertiesComponent implements OnInit
{
    connectionPickerOpen = false;
    connectionPickerData: ConfirmWithSelectionDialogInput = null;
    connectionPickerInstitutionId: string = null;
    plaidSearchOpen = false;
    existingConnectionOpen = false;
    existingConnectionBankName = '';
    existingConnectionInstitutionId: string = null;
    existingConnectionToken: PlaidToken = null;

    @Input() replacementCandidates: UnifiedBankAccount[] = [];
    @Input() updateAccount: UnifiedBankAccount = null;
    @Input() action: 'add_deposit' | 'remove_deposit' | 'link_pick' | 'link_connect' | null;
    @Input() onlyDepositAccounts: boolean;
    @Input() treasuryRestricted: boolean = false;
    // SD-10930: a Treasury org links Plaid on an account support switched to Plaid. Only the name and
    // the Plaid feed are editable; banking details and deposit linkage stay locked.
    @Input() plaidLinkOnly: boolean = false;

    @ViewChild('tokenSelect') tokenSelect: ElementRef<HTMLElement & { value: string | number | null }>;
    @ViewChild('accountSelect') accountSelect: ElementRef<HTMLElement & { value: string | number | null }>;

    form: UntypedFormGroup = null;
    jwt: JwtLegFiClaims;

    allTokens: PlaidToken[] = [];
    tokenOptions: PlaidToken[] = [];
    plaidObject: any = null;

    constructor(
            private _builder: UntypedFormBuilder,
            private _growler: GrowlerService,
            private _plaidService: PlaidService,
            private _plaidTokens: PlaidTokenService,
    ) {
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();

        this.form = this._builder.group({
            friendlyName: [
                this.updateAccount?.friendlyName || '',
                [Validators.required],
            ],
            accountNumber: [
                '',
                [
                    conditionalValidator(() => {
                        return (this.form.controls['useForDeposits'].value && !this.updateAccount?.depositBankAccount) && !this.updateAccount?.last4;
                    }, Validators.required),
                    Validators.minLength(6),
                ],
            ],
            routingNumber: [
                this.updateAccount?.routingNumber,
                [
                    conditionalValidator(() => {
                        return this.form.controls['useForDeposits'].value && !this.updateAccount?.depositBankAccount;
                    }, Validators.required),
                    Validators.minLength(9),
                    Validators.maxLength(9),
                ],
            ],
            useForDeposits: [
                {
                    value: this.onlyDepositAccounts || !!this.updateAccount?.depositBankAccount,
                    disabled: this.onlyDepositAccounts,
                } || false,
            ],
            replacementDepositAccount: [
                null,
                [
                    conditionalValidator(() => {
                        return !this.form.controls['useForDeposits'].value && this.updateAccount?.depositBankAccount;
                    }, Validators.required),
                ],
            ],
            allianceTransactionImportEnabled: [!!this.updateAccount?.allianceTransactionImportEnabled || false],
            allianceTransactionImportAfter: [
                (this.updateAccount?.allianceTransactionImportAfter || moment().startOf('day')).format('YYYY-MM-DD'),
            ],
            connectToTransactionFeed: [!!this.updateAccount?.plaidToken || false],
            plaidToken: [
                this.updateAccount?.plaidToken,
                [
                    conditionalValidator(() => {
                        return this.form.controls['connectToTransactionFeed'].value;
                    }, Validators.required),
                ],
            ],
            plaidAccount: [
                this.updateAccount?.plaidAccount,
                [
                    conditionalValidator(() => {
                        return this.form.controls['connectToTransactionFeed'].value;
                    }, Validators.required),
                ],
            ],
            plaidImportAfter: [
                (this.updateAccount?.plaidImportAfter || moment()).format('YYYY-MM-DD'),
                [
                    conditionalValidator(() => {
                        return this.form.controls['connectToTransactionFeed'].value;
                    }, Validators.required),
                ],

            ],
        });

        if (this.updateAccount?.depositBankAccount && !this.jwt.superUser) {
            this.form.get('routingNumber').disable();
        }

        // Stripe Treasury orgs may only rename a non-managed account. Lock every other control so
        // banking details and deposit/transaction linkage can't be changed in a way that would
        // break the Stripe Financial Account.
        if (this.treasuryRestricted) {
            Object.keys(this.form.controls).forEach((controlKey) => {
                if (controlKey !== 'friendlyName') {
                    this.form.get(controlKey).disable();
                }
            });
        }

        if (this.plaidLinkOnly) {
            const editable = ['friendlyName', 'connectToTransactionFeed', 'plaidToken', 'plaidAccount', 'plaidImportAfter'];
            Object.keys(this.form.controls).forEach((controlKey) => {
                if (!editable.includes(controlKey)) {
                    this.form.get(controlKey).disable();
                }
            });
        }

        if (this.action) {
            switch (this.action) {
                case 'add_deposit':
                    this.form.controls['useForDeposits'].setValue(true);
                    break;
                case 'remove_deposit':
                    this.form.controls['useForDeposits'].setValue(false);
                    break;
                case 'link_pick':
                    this.form.controls['connectToTransactionFeed'].setValue(true);
                    break;
                case 'link_connect':
                    this.form.controls['connectToTransactionFeed'].setValue(true);
                    this.launchPlaidIfNoTokens(true);
                    break;
            }
        }

        this.form.get('useForDeposits').valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.isValid(false));
        this.form.get('connectToTransactionFeed').valueChanges.pipe(untilDestroyed(this))
                .subscribe((checked: boolean) => this.launchPlaidIfNoTokens(checked));

        this._plaidTokens.$tokens.pipe(untilDestroyed(this)).subscribe({
            next: (tokens: PlaidToken[]) => {
                this.allTokens = tokens;
                if (this.form.get('plaidToken').value) {
                    this.form.get('plaidToken')
                            .setValue(this.allTokens.find(t => t.id === this.form.get('plaidToken').value.id));
                }
            },
        });
    }

    /**
     * Updates existing Plaid token to add new accounts from same banking credentials.
     * Handles Plaid Link in update mode to avoid creating duplicate connections.
     */
    updateToken(token: PlaidToken) {
        const plaidConfig = {
            onSuccess: () => {
                this.plaidObject.destroy();
                this.plaidObject = null;
                // select the first new account on the token
                // we need to refresh this token to know what was added
                this._plaidTokens.getToken(token.id).pipe(untilDestroyed(this))
                        .subscribe({
                            next: (updatedToken: PlaidToken) => {
                                const accounts = updatedToken.plaidAccounts.filter((account: PlaidAccount) => !account.unifiedBankAccount);
                                if (accounts.length === 0) {
                                    this._growler.oops('No new accounts were detected. Please try connecting again.');
                                } else {
                                    this.form.controls['plaidAccount'].setValue(accounts[0]);
                                }
                            },
                        });
            },
            onExit: (error: any) => {
                this.plaidObject.destroy();
                if (error !== null) {
                    // an error occurred in plaid
                    this._growler.oops('An error occurred during the linking process. Please try again.');
                } else {
                    // The user exited plaid
                    this._growler.oops('You must complete the Plaid link process to enable transactions. Please try again.');
                }
            },
            token: token.updateLinkToken,
        };

        this.plaidObject = Plaid.create(plaidConfig);
        this.plaidObject.open();
    }

    launchPlaidIfNoTokens(checked: boolean) {
        if (!checked) {
            return;
        }
        if (this.allTokens.length === 0) {
            // launch straight into plaid if we have no tokens at all
            this.connectToBank();
        }
    }

    /**
     * Initiates institution search flow with smart duplicate detection.
     * Prompts user to either update existing connections or create new ones
     * when attempting to connect to an already-linked institution.
     */
    searchBanks() {
        this.plaidSearchOpen = true;
    }

    onPlaidSearchClosed(result: PlaidSearchDialogResult) {
        this.plaidSearchOpen = false;
        if (!result?.success) {
            return;
        }
        const alreadyConnectedToBank = this.allTokens.filter(token => token.plaidInstitutionId === result?.selection?.institutionId && !token.plaidError);
        if (!alreadyConnectedToBank.length) {
            return this.connectToBank(result?.selection?.institutionId);
        }
        if (alreadyConnectedToBank.length === 1) {
            this.existingConnectionBankName = result?.selection.name;
            this.existingConnectionInstitutionId = result?.selection.institutionId;
            this.existingConnectionToken = alreadyConnectedToBank[0];
            this.existingConnectionOpen = true;
        } else {
            this.connectionPickerInstitutionId = result?.selection.institutionId;
            this.connectionPickerData = <ConfirmWithSelectionDialogInput>{
                title: 'Confirm Bank Selection',
                content: 'PayHOA already has multiple connections to ' + result?.selection.name + '. Do you want to add an additional account to an existing connection, or connect a new online banking login?.',
                btnContent: 'Add Account to Selected Existing Connection',
                secondaryBtnContent: 'Connect a New Account',
                selectionLabel: 'Select Connection to Update',
                selectionRequired: true,
                selectionRequiredSecondary: false,
                selections: alreadyConnectedToBank,
            };
            this.connectionPickerOpen = true;
        }
    }

    onExistingConnectionClosed(choice: ExistingBankConnectionChoice) {
        this.existingConnectionOpen = false;
        if (choice === 'new') {
            return this.connectToBank(this.existingConnectionInstitutionId);
        }
        if (choice === 'existing') {
            return this.updateToken(this.existingConnectionToken);
        }
    }

    onConnectionPickerClosed(confirmResult: ConfirmWithSelectionDialogResult | null) {
        this.connectionPickerOpen = false;
        if (confirmResult?.success) {
            if (confirmResult?.secondaryBtnClicked) {
                return this.connectToBank(this.connectionPickerInstitutionId);
            } else {
                return this.updateToken(<PlaidToken>confirmResult?.selection);
            }
        }
    }

    connectToBank(institutionId: string = null) {
        this._plaidService.getLinkToken().pipe(untilDestroyed(this)).subscribe({
            next: (token) => {
                const plaidConfig = {
                    onSuccess: (publicToken: any, metadata: PlaidSuccessMetaData) => {
                        this.plaidObject.destroy();
                        this._plaidTokens.createToken(publicToken, metadata.institution.institution_id).pipe(untilDestroyed(this)).subscribe({
                            next: (newPlaidToken: PlaidToken) => {
                                // select the first account on the new token
                                this.form.patchValue({
                                    plaidAccount: newPlaidToken.plaidAccounts[0],
                                });
                            },
                        });
                    },
                    onExit: (error: any) => {
                        // if we don't have any other tokens to pick from, toggle transactions back off
                        if (this.tokenOptions.length === 0) {
                            this.form.get('connectToTransactionFeed').setValue(false);
                        }
                        this.plaidObject.destroy();
                        if (error !== null) {
                            // an error occurred in plaid
                            this._growler.oops('An error occurred during the linking process. Please try again.');
                        } else {
                            // The user exited plaid
                            this._growler.oops('You must complete the Plaid link process to enable transactions. Please try again.');
                        }
                    },
                    token: token,
                };

                this.plaidObject = Plaid.create(plaidConfig);
                this.plaidObject.open(institutionId);
            },
            error: () => {
                // error fetching link token
                this._growler.oops('An error occurred initializing your link session. Please try again.');
            },
        });
    }

    isValid(growl: boolean = true) {
        for (const controlKey in this.form.controls) {
            if (!this.form.controls.hasOwnProperty(controlKey)) {
                continue;
            }
            const control = this.form.controls[controlKey];
            control.markAsTouched();
            control.updateValueAndValidity();
        }

        if (!this.form.valid) {
            if (growl) {
                this._growler.error('Error', 'Please enter all required fields');
            }
            return false;
        }
        return true;
    }

    /** Resolves the picked Plaid account to the token and account ids the API takes. */
    private withPlaidFeedIds<T extends { connectToTransactionFeed?: boolean; plaidAccount?: PlaidAccount; plaidTokenId?: number; plaidAccountId?: string }>(request: T): T {
        if (request.connectToTransactionFeed) {
            const tokenForSelectedAccount: PlaidToken = this.allTokens.filter(token => {
                return token.plaidAccounts.map(account => account.accountId).includes(request.plaidAccount.accountId);
            })[0];
            request.plaidTokenId = tokenForSelectedAccount.id;
            request.plaidAccountId = request.plaidAccount.accountId;
        }
        return request;
    }

    /**
     * Transforms form data for API submission with sensitive field handling.
     * Omits unchanged routing/account numbers for security and only includes
     * modified deposit settings to prevent accidental state changes.
     */
    getFormValue(): BankAccountPropertiesFormValue {
        const request = this.form.getRawValue();

        // Treasury-restricted accounts may only change the display name — never banking details,
        // deposit linkage, or transaction-feed connections that could break the Financial Account.
        if (this.treasuryRestricted) {
            return {friendlyName: request.friendlyName} as BankAccountPropertiesFormValue;
        }

        if (this.plaidLinkOnly) {
            const feed = this.withPlaidFeedIds(request);
            return {
                friendlyName: feed.friendlyName,
                connectToTransactionFeed: feed.connectToTransactionFeed,
                plaidTokenId: feed.plaidTokenId,
                plaidAccountId: feed.plaidAccountId,
                plaidImportAfter: moment(feed.plaidImportAfter).format('YYYY-MM-DD') as RequestDate,
            } as BankAccountPropertiesFormValue;
        }

        // don't include the deposit flag if set and not changed
        if (this.updateAccount?.depositBankAccount) {
            if (request.useForDeposits) {
                delete request.useForDeposits;
            }
        }

        // omit the routing and account numbers if editing and unchanged
        if (this.updateAccount) {
            // if the routing number didn't change, remove it from the request
            if (request.routingNumber.trim() === this.updateAccount.routingNumber) {
                delete request.routingNumber;
            }

            // if the account number is empty, it didn't change, remove it
            if (request.accountNumber.trim() === '') {
                delete request.accountNumber;
            }
        }

        // don't include transaction related fields if not changed
        this.withPlaidFeedIds(request);
        delete request.plaidAccount;

        if (request.hasOwnProperty('allianceTransactionImportAfter')) {
            request.allianceTransactionImportAfter = moment(request.allianceTransactionImportAfter).format('YYYY-MM-DD');
        }
        if (request.hasOwnProperty('plaidImportAfter')) {
            request.plaidImportAfter = moment(request.plaidImportAfter).format('YYYY-MM-DD');
        }

        return request;
    }

    get replacementItems(): PhSelectItem[] {
        return this.replacementCandidates.map(account => ({value: account.id, label: account.friendlyName}));
    }

    get tokenItems(): PhSelectItem[] {
        return [
            ...this.allTokens.map(token => ({
                value: token.id,
                label: (token.propertyManagementCompanyId ? 'PMC: ' : '') + token.institutionName + ' - '
                        + (token.plaidError ? 'Needs Attention' : token.plaidAccounts.length + ' Accounts'),
            })),
            {value: NEW_LOGIN, label: 'Add new Bank Login'},
        ];
    }

    get accountItems(): PhSelectItem[] {
        const token: PlaidToken = this.form.get('plaidToken').value;
        return [
            ...(token?.plaidAccounts || []).map(account => ({
                value: account.accountId,
                label: account.name + ' - ****' + account.mask,
                disabled: !!account.unifiedBankAccount && account.unifiedBankAccount.id !== this.updateAccount?.id,
            })),
            {value: NEW_ACCOUNT, label: 'Connect Another Account'},
        ];
    }

    get selectedTokenId(): number | null {
        return this.form.get('plaidToken').value?.id ?? null;
    }

    get selectedAccountId(): string | null {
        return this.form.get('plaidAccount').value?.accountId ?? null;
    }

    showError(controlName: string): boolean {
        const control = this.form.get(controlName);
        return control.touched && control.invalid;
    }

    toggleControl(controlName: string) {
        const control = this.form.get(controlName);
        if (control.enabled) {
            control.setValue(!control.value);
        }
    }

    onTokenSelected(value: string | number) {
        const control = this.form.get('plaidToken');
        control.markAsTouched();
        if (value === NEW_LOGIN) {
            control.setValue(null);
            if (this.tokenSelect) {
                this.tokenSelect.nativeElement.value = null;
            }
            this.searchBanks();
            return;
        }
        control.setValue(this.allTokens.find(token => token.id === value) || null);
    }

    onAccountSelected(value: string | number) {
        const control = this.form.get('plaidAccount');
        control.markAsTouched();
        if (value === NEW_ACCOUNT) {
            control.setValue(null);
            if (this.accountSelect) {
                this.accountSelect.nativeElement.value = null;
            }
            this.updateToken(this.form.get('plaidToken').value);
            return;
        }
        const token: PlaidToken = this.form.get('plaidToken').value;
        control.setValue(token?.plaidAccounts.find(account => account.accountId === value) || null);
    }
}
